/**
 * Pure rules of the public onboarding page: what the form may send, when it may be submitted, what a
 * file must satisfy before it is even sent. No React, no network - so the rules are testable alone.
 */
import type {
  DataSlot,
  LeadAnswers,
  LeadProfile,
  PublicFileView,
  PublicLeadView,
  PublicSlotInfo,
} from "../OnboardingAPI";

/** Slots the contract calls required for the submit check (a slot's own `required` flag decides). */
export const LOGO_SLOT: DataSlot = "logo";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * The first and the last name out of one contact name (an older lead has only that): the last word is the last
 * name, what stands before it the first name; "Müller, Eva" is read the other way round. One word is a last name.
 */
export function splitContactName(full: string | undefined | null): { first: string; last: string } {
  const tokens = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { first: "", last: "" };
  if (tokens.length >= 2 && tokens[0].endsWith(",")) return { first: tokens.slice(1).join(" "), last: tokens[0].replace(/,+$/, "") };
  const last = tokens[tokens.length - 1];
  return { first: tokens.slice(0, -1).join(" "), last };
}

/** "First Last" for a summary: from the two names, else from the contact name the server made. */
export const fullName = (profile: { contactFirstName?: string; contactLastName?: string; contactName?: string }): string => {
  const joined = [profile.contactFirstName, profile.contactLastName].map((part) => (part ?? "").trim()).filter(Boolean).join(" ");
  return joined || (profile.contactName ?? "").trim();
};

export const isValidEmail = (value: string | undefined): boolean => EMAIL_PATTERN.test((value ?? "").trim());

/** Editable text fields of the profile, in display order. */
export const PROFILE_TEXT_FIELDS = [
  "companyName",
  "legalName",
  "street",
  "streetNumber",
  "postalCode",
  "city",
  "country",
  "website",
  "contactFirstName",
  "contactLastName",
  "contactPhone",
] as const;

export const ANSWER_TEXT_FIELDS = ["labelNumberNote", "criticalAreas", "floorplanVersionNote", "sourceSystem", "escapePlansNote"] as const;

/**
 * The server's limits per text field (tri-server `src/onboarding/leadRules.ts`). The server refuses
 * the WHOLE body for one bad field, so the form mirrors them: a field that breaks one is shown with a
 * message and held back from the autosave, and every other field keeps being saved.
 */
export const FIELD_MAX: Record<string, number> = {
  companyName: 200,
  legalName: 200,
  street: 200,
  streetNumber: 30,
  postalCode: 20,
  city: 120,
  country: 120,
  website: 300,
  contactFirstName: 100,
  contactLastName: 100,
  contactEmail: 254,
  contactPhone: 40,
  labelNumberNote: 500,
  criticalAreas: 2000,
  floorplanVersionNote: 500,
  sourceSystem: 200,
  escapePlansNote: 300,
};

const MULTILINE_FIELDS = new Set(["labelNumberNote", "criticalAreas", "floorplanVersionNote", "escapePlansNote"]);

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

/** The server's cleaning: control characters out, line breaks kept only in the long fields, trimmed. */
export const cleanFieldText = (value: string, multiline = false): string => {
  const withoutControl = value.replace(CONTROL_CHARS, "");
  return (multiline ? withoutControl.replace(/\r\n?/g, "\n") : withoutControl.replace(/[\r\n\t]+/g, " ")).trim();
};

/** The server's URL rule: http(s), and a host with a dot; a missing scheme is added. */
export const isValidWebsite = (value: string): boolean => {
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(candidate);
    return (url.protocol === "http:" || url.protocol === "https:") && !!url.hostname && url.hostname.includes(".");
  } catch {
    return false;
  }
};

export type FieldProblem = "tooLong" | "invalidWebsite";

export interface FormValues {
  profile: LeadProfile;
  answers: LeadAnswers;
}

/** The problem a field has by the server's rules, or null. An empty field is never a problem here (a required one only blocks the submit). */
export function fieldProblem(field: string, raw: string | undefined): FieldProblem | null {
  const text = cleanFieldText(raw ?? "", MULTILINE_FIELDS.has(field));
  if (!text) return null;
  if (FIELD_MAX[field] !== undefined && text.length > FIELD_MAX[field]) return "tooLong";
  if (field === "website" && !isValidWebsite(text)) return "invalidWebsite";
  return null;
}

/** Every field of the form that breaks a rule, by field name. */
export function fieldProblems(values: FormValues): Record<string, FieldProblem> {
  const found: Record<string, FieldProblem> = {};
  PROFILE_TEXT_FIELDS.forEach((field) => {
    const problem = fieldProblem(field, values.profile[field]);
    if (problem) found[field] = problem;
  });
  ANSWER_TEXT_FIELDS.forEach((field) => {
    const problem = fieldProblem(field, values.answers[field]);
    if (problem) found[field] = problem;
  });
  return found;
}

/**
 * The body of `PUT /profile` for the current form values.
 *
 * - A field that breaks the server's rules ({@link fieldProblem}) is left out, so one bad field cannot
 *   make the server refuse the others.
 * - The three required profile fields are sent only while they hold something valid: a half-typed
 *   address or a cleared name must not be turned into a refusal while the person is still typing.
 *   The submit button holds those back instead.
 * - Optional text fields are sent trimmed, an emptied one as an empty string, so clearing a field
 *   actually clears it on the server.
 * - `logoFileId` is never sent: the file itself already carries the slot `logo`.
 */
export function buildSavePayload(values: FormValues): { profile: Partial<LeadProfile>; answers: Partial<LeadAnswers> } {
  const profile: Record<string, string> = {};
  PROFILE_TEXT_FIELDS.forEach((field) => {
    const raw = (values.profile[field] ?? "").trim();
    if (fieldProblem(field, raw)) return;
    if (field === "companyName" || field === "contactFirstName" || field === "contactLastName") {
      if (raw) profile[field] = raw;
    } else {
      profile[field] = raw;
    }
  });
  if (values.profile.assetClass) profile.assetClass = values.profile.assetClass;
  if (values.profile.ownership) profile.ownership = values.profile.ownership;

  const answers: Record<string, string> = {};
  if (values.answers.stableIds) answers.stableIds = values.answers.stableIds;
  if (values.answers.labelsInstalled) answers.labelsInstalled = values.answers.labelsInstalled;
  // the escape plans answers are new: they are only sent once the person has set them
  if (values.answers.escapePlansStatus) answers.escapePlansStatus = values.answers.escapePlansStatus;
  ANSWER_TEXT_FIELDS.forEach((field) => {
    if (field === "escapePlansNote" && values.answers.escapePlansNote === undefined) return;
    const raw = (values.answers[field] ?? "").trim();
    if (fieldProblem(field, raw)) return;
    // A label number only makes sense for "yes"; a stale one is cleared, not kept.
    answers[field] = field === "labelNumberNote" && values.answers.labelsInstalled !== "yes" ? "" : raw;
  });
  return { profile: profile as Partial<LeadProfile>, answers: answers as Partial<LeadAnswers> };
}

/** Stable text for change detection: two payloads are the same save exactly when this matches. */
export const payloadKey = (values: FormValues) => JSON.stringify(buildSavePayload(values));

/** The data slots shown in "Ihre Daten": the view's order, `other` last, the logo split off. */
export function dataSlots(slots: PublicSlotInfo[]): PublicSlotInfo[] {
  const rest = slots.filter((slot) => slot.slot !== LOGO_SLOT && slot.slot !== "other");
  const other = slots.filter((slot) => slot.slot === "other");
  return [...rest, ...other];
}

export const logoSlot = (slots: PublicSlotInfo[]): PublicSlotInfo | undefined =>
  slots.find((slot) => slot.slot === LOGO_SLOT);

export const filesOfSlot = (files: PublicFileView[], slot: DataSlot) => files.filter((file) => file.slot === slot);

export type SubmitBlocker = "companyName" | "contactFirstName" | "contactLastName" | "contactPhone" | "phone" | "data" | "fields" | "uploads";

/**
 * What still stops the submit button, in the order it is shown. The server needs company, contact,
 * a valid address and at least one file in a required slot; a *missing* required slot beyond that
 * is no blocker (see {@link missingRequiredSlots}).
 */
export function submitBlockers(
  values: FormValues,
  view: Pick<PublicLeadView, "files" | "slots" | "phone">,
  uploadsRunning: boolean,
): SubmitBlocker[] {
  const blockers: SubmitBlocker[] = [];
  if (!values.profile.companyName.trim()) blockers.push("companyName");
  if (!(values.profile.contactFirstName ?? "").trim()) blockers.push("contactFirstName");
  if (!(values.profile.contactLastName ?? "").trim()) blockers.push("contactLastName");
  // once the server checks the mobile number: the number is required, and it must be confirmed by a code
  if (view.phone && !(values.profile.contactPhone ?? "").trim()) blockers.push("contactPhone");
  else if (view.phone && !view.phone.verified) blockers.push("phone");
  const requiredSlots = view.slots.filter((slot) => slot.required).map((slot) => slot.slot);
  if (!view.files.some((file) => requiredSlots.includes(file.slot))) blockers.push("data");
  // a field that breaks a rule is not saved: say so before the person believes it was
  if (Object.keys(fieldProblems(values)).length > 0) blockers.push("fields");
  if (uploadsRunning) blockers.push("uploads");
  return blockers;
}

/** Required slots without any file: a friendly note, not a blocker. */
export function missingRequiredSlots(view: Pick<PublicLeadView, "files" | "slots">): DataSlot[] {
  return view.slots
    .filter((slot) => slot.required && !view.files.some((file) => file.slot === slot.slot))
    .map((slot) => slot.slot);
}

export const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
};

export type ClientFileError = "invalid_file_type" | "file_too_large" | "too_many_files";

/**
 * Checks the server would make anyway, done before sending so a wrong file costs no upload.
 * Everything comes from the view: extensions per slot, the size limit, the total and per-slot counts.
 * The server stays the authority - a file that passes here can still be refused there.
 */
export function checkFileBeforeUpload(
  file: { name: string; size: number },
  slot: PublicSlotInfo,
  view: Pick<PublicLeadView, "files" | "limits">,
  pendingInSlot: number,
  pendingTotal: number,
): ClientFileError | null {
  const allowed = slot.extensions.map((extension) => extension.replace(/^\./, "").toLowerCase());
  if (!allowed.includes(extensionOf(file.name))) return "invalid_file_type";
  if (file.size > view.limits.maxFileSizeBytes) return "file_too_large";
  if (view.files.length + pendingTotal >= view.limits.maxFiles) return "too_many_files";
  if (slot.maxFiles !== undefined && filesOfSlot(view.files, slot.slot).length + pendingInSlot >= slot.maxFiles) {
    return "too_many_files";
  }
  return null;
}

export function formatBytes(bytes: number, locale: string): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = value >= 100 || Number.isInteger(value) ? 0 : 1;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value)} ${units[unit]}`;
}

/** The company step is done: name, contact and a valid address, and no text field breaking a rule. */
export function companyReady(values: FormValues, phoneRequired = false): boolean {
  return (
    !!values.profile.companyName.trim() &&
    !!(values.profile.contactFirstName ?? "").trim() &&
    !!(values.profile.contactLastName ?? "").trim() &&
    (!phoneRequired || !!(values.profile.contactPhone ?? "").trim()) &&
    Object.keys(fieldProblems(values)).length === 0
  );
}
